import React, { ReactNode, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { Child } from '../../interface/Child';
import { createDefaultChild } from '../../utils/childUtils';

import ChildAddCard from './ChildAddCard';
import ChildCard from './ChildCard';
import ChildEditModal from './ChildEditModal';

interface ChildrenManagementContentProps {
    children: Child[];
    onChildrenChange: (children: Child[]) => void;
    onSaveChild?: (child: Child) => Promise<void>;
    onDeleteChild?: (child: Child) => Promise<void>;
    isLoading?: boolean;
    parentId?: string;
    parentFirstName?: string;
    parentLastName?: string;
    parentPhone?: string;
    headerComponent?: ReactNode;
    footerComponent?: ReactNode;
}

const ChildrenManagementContent: React.FC<ChildrenManagementContentProps> = ({
    children: childrenProp,
    onChildrenChange,
    onSaveChild,
    onDeleteChild,
    isLoading = false,
    parentId = '',
    parentFirstName = '',
    parentLastName = '',
    parentPhone = '',
    headerComponent,
    footerComponent,
}) => {
    const [children, setChildren] = useState<Child[]>(childrenProp);
    const [editingChild, setEditingChild] = useState<Child | null>(null);
    const [isNewChild, setIsNewChild] = useState(false);
    const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});
    const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
    const [originalChildData, setOriginalChildData] = useState<Child | null>(null);

    useEffect(() => {
        setChildren(childrenProp);
    }, [childrenProp]);

    const handleAddChild = () => {
        const newChild = createDefaultChild(parentId, parentFirstName, parentLastName, parentPhone);
        const updated = [...children, newChild];
        setChildren(updated);
        onChildrenChange(updated);
        setIsNewChild(true);
        setEditingChild(newChild);
    };

    const handleStartEdit = (child: Child) => {
        setOriginalChildData(JSON.parse(JSON.stringify(child)));
        setIsNewChild(false);
        setEditingChild(child);
    };

    const handleCancel = () => {
        if (isNewChild && editingChild) {
            const updated = children.filter(c => c.userId !== editingChild.userId);
            setChildren(updated);
            onChildrenChange(updated);
        } else if (originalChildData && editingChild) {
            const updated = children.map(c =>
                c.userId === editingChild.userId ? originalChildData : c
            );
            setChildren(updated);
            onChildrenChange(updated);
        }
        setOriginalChildData(null);
        setEditingChild(null);
    };

    const handleSave = async (updatedChild: Child) => {
        const id = updatedChild.userId;
        setSavingMap(m => ({ ...m, [id]: true }));
        try {
            if (onSaveChild) await onSaveChild(updatedChild);
            const updated = children.map(c => c.userId === id ? updatedChild : c);
            setChildren(updated);
            onChildrenChange(updated);
            setOriginalChildData(null);
            setEditingChild(null);
        } catch (err) {
            console.error('Error saving child:', err);
        } finally {
            setSavingMap(m => ({ ...m, [id]: false }));
        }
    };

    const handleDelete = async (child: Child) => {
        const id = child.userId;
        if (!window.confirm(`Remove ${child.firstName} ${child.lastName} from your account?`)) return;
        setDeletingMap(m => ({ ...m, [id]: true }));
        try {
            if (onDeleteChild) await onDeleteChild(child);
            const updated = children.filter(c => c.userId !== id);
            setChildren(updated);
            onChildrenChange(updated);
            if (editingChild?.userId === id) setEditingChild(null);
        } catch (err) {
            console.error('Error deleting child:', err);
        } finally {
            setDeletingMap(m => ({ ...m, [id]: false }));
        }
    };

    if (isLoading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#3C8248" />
                <Text style={styles.loadingText}>Loading family members...</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {headerComponent}

            <View style={styles.childrenContainer}>
                <ChildAddCard onPress={handleAddChild} />

                {children.map(child => (
                    <ChildCard
                        key={child.userId}
                        child={child}
                        onEdit={handleStartEdit}
                        onDelete={() => handleDelete(child)}
                        isSaving={deletingMap[child.userId]}
                    />
                ))}

                {children.length === 0 && (
                    <View style={styles.emptyState}>
                        <Text style={styles.emptyStateText}>
                            No family members added yet. Tap above to add your first family member.
                        </Text>
                    </View>
                )}
            </View>

            {editingChild && (
                <ChildEditModal
                    visible
                    child={editingChild}
                    isNew={isNewChild}
                    isSaving={savingMap[editingChild.userId]}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    parentFirstName={parentFirstName}
                    parentLastName={parentLastName}
                    parentPhone={parentPhone}
                />
            )}

            {footerComponent}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
    },
    childrenContainer: {
        width: '100%',
        gap: 16,
    },
    emptyState: {
        paddingVertical: 40,
        alignItems: 'center',
    },
    emptyStateText: {
        fontSize: 14,
        color: '#9CA3AF',
        fontWeight: '500',
        textAlign: 'center',
    },
    loadingContainer: {
        paddingVertical: 40,
        alignItems: 'center',
    },
    loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: '#6B7280',
    },
});

export default ChildrenManagementContent;
